// @ts-check
import { Component } from '@verndale/core';
import { createFocusTrap } from 'focus-trap';
import { KEYCODE } from '../utils';

export const OPEN_CART_EVENT = 'OPEN_CART';

class CartModal extends Component {
  setupDefaults() {
    this.dom = {
      $cartModal: this.el,
      $modalContent: this.el.querySelector('.cart-modal__content'),
      $openModalButton: document.querySelector('.site-selector__cart'),
      $openModalButtonCount: document.querySelector(
        '.site-selector__cart span'
      ),
      $closeModalButton: this.el.querySelector('.cart-modal__close-button'),
      $checkoutButton: this.el.querySelector('.cart-modal__checkout-button'),
      $subtotal: this.el.querySelector('.cart-modal__item-subtotal')
    };

    this.endpoint = this.el.dataset.endpoint;
    this.cartModalTrap = createFocusTrap(this.el, {
      escapeDeactivates: true,
      clickOutsideDeactivates: true
    });

    this.fetchCartItems();
  }

  addListeners() {
    this.el.addEventListener('keydown', (e) => this.handleCartKeydown(e));
    this.el.addEventListener('focusout', this.handleFocusOut.bind(this));
    this.dom.$openModalButton.addEventListener(
      'click',
      this.handleTrigger.bind(this)
    );
    this.dom.$closeModalButton.addEventListener(
      'click',
      this.closeModal.bind(this)
    );
    window.addEventListener(OPEN_CART_EVENT, () => this.handleCartUpdate());
  }

  handleCartUpdate() {
    this.fetchCartItems();
    this.openModal();
  }

  handleTrigger(e) {
    const isExpanded = e.currentTarget.getAttribute('aria-expanded') === 'true';

    if (isExpanded) this.closeModal(e);
    else this.openModal(e);
  }

  cleanItemsData(items) {
    return items.map((item, index) => {
      const cleanedItem = {
        index,
        item_id: item.CartItemId,
        item_name: item.Title,
        item_brand: item.Brand,
        price: Number(item.Price),
        quantity: Number(item.Quantity),
        affiliation: 'EatRightStore',
        ...(item.Categories || []).reduce((acc, category, i) => {
          acc[`item_category${i + 1}`] = category.Heading;
          return acc;
        }, {})
      };

      // Remove keys that are undefined or empty
      Object.keys(cleanedItem).forEach(
        (key) =>
          (cleanedItem[key] === undefined || cleanedItem[key] === '') &&
          delete cleanedItem[key]
      );

      return cleanedItem;
    });
  }

  openModal(e) {
    e?.preventDefault();
    this.dom.$openModalButton.setAttribute('aria-expanded', 'true');
    this.cartModalTrap?.activate?.();
  }

  closeModal(e) {
    e?.preventDefault();
    this.dom.$openModalButton.setAttribute('aria-expanded', 'false');
    this.cartModalTrap?.deactivate?.();
  }

  handleFocusOut(e) {
    if (
      !e.currentTarget.contains(e.relatedTarget) &&
      e.relatedTarget !== this.dom.$openModalButton
    ) {
      this.closeModal();
    }
  }

  handleCartKeydown(e) {
    if (e.keyCode === KEYCODE.ESCAPE) {
      e.stopPropagation();
      this.dom.$openModalButton.setAttribute('aria-expanded', 'false');
    }
  }

  renderModalContent(data) {
    this.cartData = data;

    this.dom.$subtotal.innerHTML = `${data.ItemsCount} item(s) Subtotal <span>$${data.Total}</span>`;
    const cartCounter = this.dom.$openModalButtonCount;
    if (cartCounter instanceof HTMLElement) {
      cartCounter.innerHTML = cartCounter.dataset.cartLabel.replace(
        '{0}',
        `${data.ItemsCount}`
      );
    }
    this.dom.$modalContent.innerHTML = this.createItemCards(data.Items);
  }

  hasPromo(item) {
    return item?.Promo?.PromoTitle || item?.Promo?.PromoSubtitle || item?.Promo?.PromoDetails;
  }

  renderPromoBanner(item) {
    const promoTitle = item?.Promo?.PromoTitle;
    const promoSubtitle = item?.Promo?.PromoSubtitle;
    const promoDetailsLabel = item?.Promo?.PromoDetailsLabel;
    const promoDetails = item?.Promo?.PromoDetails;
    const hasPromo = this.hasPromo(item);

    if (!hasPromo) return '';

    return `
      <div class="promotion-banner">
        <div class="promotion-banner__title">
          <svg viewBox="0 0 1024 1024" aria-hidden="true">
            <use xlink:href="#dollar"></use>
          </svg>

          <div class="promotion-banner__title-text">
            ${promoTitle ? `<strong>${promoTitle}</strong>` : ''}
            ${promoSubtitle}
          </div>
        </div>

        ${
          promoDetails
            ? `
          <details>
            <summary>${promoDetailsLabel}</summary>
            <p>${promoDetails}</p>
          </details>
        `
            : ''
        }
      </div>
    `;
  }

  createItemCards(items) {
    return `
    ${items
      .map(
        (item) => `
    <div class="cart-modal__item">
      ${this.renderPromoBanner(item)}
      <div class="cart-modal__item-preview">
        <img src="${item.Thumbail}" alt="${
          item.Title
        }" width="200" height="110">
        <div class="cart-modal__item-details">
          <h2 class="cart-modal__item-title">
            <a href="${item.ProductUrl}">${item.Title}</a>
          </h2>
          <p class="cart-modal__item-type">${item.ProductType}</p>
          <p class="cart-modal__item-sku">SKU#${item.SKU}</p>
          <p class="cart-modal__item-price">${item.Price}</p>
        </div>
      </div>
      <table class="cart-modal__item-pricing">
        <thead>
          <tr>
            <th>QTY</th>
            <th>Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>${item.Quantity}</td>
            ${this.hasPromo(item) ? `<td><s>${item.Price}</s></td>` : `<td>${item.Price}</td>`}
            <td>${item.TotalPrice}</td>
          </tr>
        </tbody>
      </table>
    </div>
    `
      )
      .join('')}
    `;
  }

  fetchCartItems() {
    fetch(this.endpoint)
      .then((response) => response.json())
      .then(({ data }) => this.renderModalContent(data))
      .catch(console.error);
  }
}

export default CartModal;
